import React, { useState, useEffect } from 'react';
import { uploadCertificateTemplate } from '../../../services/CommonService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Certificate.css';

const UploadCertificateTemplate = ({ isOpen, onClose, onUploadSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    template_name: '',
    description: '',
    file: null,
  });

  const [errors, setErrors] = useState({
    template_name: '',
    description: '',
    file: '',
  });

  const [isDragOver, setIsDragOver] = useState(false);

  // Reset form data when the modal is opened or closed
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        template_name: '',
        description: '',
        file: null,
      });
      setErrors({
        template_name: '',
        description: '',
        file: '',
      });
      setIsDragOver(false);
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {
      template_name: '',
      description: '',
      file: '',
    };

    // Validate Template Name
    if (!formData.template_name.trim()) {
      newErrors.template_name = 'Template Name is required';
    }

    // Validate Description
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    // Validate File
    if (!formData.file) {
      newErrors.file = 'Please select a PDF file';
    } else if (formData.file.type !== 'application/pdf') {
      newErrors.file = 'Only PDF files are supported';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    setFormData((prev) => ({
      ...prev,
      file: file,
    }));

    // Clear file error when user selects a file
    if (errors.file) {
      setErrors(prev => ({
        ...prev,
        file: '',
      }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setFormData((prev) => ({
          ...prev,
          file: file,
        }));
        // Clear file error when user drops a valid file
        if (errors.file) {
          setErrors(prev => ({
            ...prev,
            file: '',
          }));
        }
      } else {
        toast.error('Only PDF files are supported');
      }
    }
  };

  const handleUploadCertificate = async () => {
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    const uploadData = new FormData();
    uploadData.append('template_name', formData.template_name);
    uploadData.append('description', formData.description);
    uploadData.append('file', formData.file);

    setLoading(true);
    try {
      const response = await uploadCertificateTemplate(uploadData);

      // Normalize response data object for callback
      let processedResponse = response;

      if (response && typeof response === 'object' && response.data) {
        processedResponse = response.data;
      } else if (typeof response === 'string') {
        try {
          processedResponse = JSON.parse(response);
        } catch {
          processedResponse = response;
        }
      }

      toast.success('Certificate template uploaded successfully!');
      onClose();

      if (onUploadSuccess) {
        onUploadSuccess(processedResponse);
      }

      // Reset form after successful upload
      setFormData({
        template_name: '',
        description: '',
        file: null,
      });
      setErrors({
        template_name: '',
        description: '',
        file: '',
      });
    } catch (error) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      template_name: '',
      description: '',
      file: null,
    });
    setErrors({
      template_name: '',
      description: '',
      file: '',
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {loading && <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>}
      <div
        className="offcanvas offcanvas-end modern-upload-panel show"
        tabIndex={-1}
        aria-labelledby="uploadCertificateOffcanvasLabel"
        data-bs-scroll="false"
        data-bs-backdrop="static"
        data-bs-keyboard="true"
        id="uploadCertificateOffcanvas"
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title" id="uploadCertificateOffcanvasLabel">
            Upload Certificate
          </h5>
          <button type="button" className="btn-close" onClick={handleClose} aria-label="Close">
            <i className="bi bi-x-circle"></i>
          </button>
        </div>

        <div className="offcanvas-body modern-body">
          <div className="modern-container mb-5">
            <div className="modern-document-details-card mb-0">
              <div className="modern-card-header mb-1">
                <label htmlFor="template_name" className="modern-label">
                  Template Name *
                </label>
              </div>
              <div className="modern-form-group">
                <input
                  type="text"
                  className={`modern-input ${errors.template_name ? "modern-input-error" : ""}`}
                  id="template_name"
                  name="template_name"
                  value={formData.template_name}
                  onChange={handleInputChange}
                  placeholder="Enter template name"
                  required
                  disabled={loading}
                  autoComplete="off"
                />
                {errors.template_name && (
                  <div className="certificate-error-message">
                    {errors.template_name}
                  </div>
                )}
              </div>
              <div className="modern-card-header mb-1">
                <label htmlFor="description" className="modern-label">
                  Description *
                </label>
              </div>
              <div className="modern-form-group">
                <textarea
                  className={`modern-input ${errors.description ? "modern-input-error" : ""}`}
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter template description"
                  rows={3}
                  required
                  disabled={loading}
                />
                {errors.description && (
                  <div className="certificate-error-message">
                    {errors.description}
                  </div>
                )}
              </div>
            </div>

            <div className="modern-upload-section mt-4">
              <div className="modern-upload-header mb-1">
                <label className="modern-label">
                  <i className="bi bi-cloud-upload me-2"></i>
                  Upload Certificate *
                </label>
              </div>
              {/* Show selected file info */}
              {formData.file && (
                <div className="modern-file-item">
                  <div className="modern-file-info">
                    <i className="bi bi-file-earmark-pdf modern-file-icon"></i>
                    <div className="modern-file-name">
                      <span className="file-name">{formData.file.name}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="modern-remove-btn"
                    onClick={() => setFormData(prev => ({ ...prev, file: null }))}
                    title="Remove file"
                  >
                    <i className="bi bi-x-circle-fill" aria-hidden="true" />
                  </button>
                </div>
              )}
              <div className="modern-upload-container">
                <div className={`modern-drag-area ${isDragOver ? 'modern-dragging' : ''} ${errors.file ? 'modern-drag-area-error' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}>
                  <div className="modern-upload-content" onClick={() => document.getElementById('certificateFile').click()}>
                    <i className="bi bi-cloud-upload modern-upload-icon"></i>
                    <p className="modern-upload-description">
                      Drag & drop files here, or click to browse and select files from your device.
                    </p>
                  </div>
                  <input
                    type="file"
                    className="file-input-hidden"
                    id="certificateFile"
                    onChange={handleFileChange}
                    accept=".pdf"
                    disabled={loading}
                  />
                </div>
                <div className="modern-form-text mt-2">Supported format: PDF</div>
                {errors.file && (
                  <div className="certificate-error-message">
                    {errors.file}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer border-top py-2">
            <button type="button" className="btn-back-outline" onClick={handleClose} disabled={loading}>
              Cancel
            </button>
            <button type="button" className="btn-save" onClick={handleUploadCertificate} disabled={loading}>
              {loading ? 'Uploading...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Offcanvas backdrop */}
      <div className="offcanvas-backdrop fade show" onClick={handleClose}></div>
    </>
  );
};

export default UploadCertificateTemplate;
